import { useMemo } from 'react';

/**
 * The picture that says what the product is.
 *
 * A sentence claiming the estate is joined up is a claim. This is the shape of
 * the join itself, drawn: a large application estate underneath, the products
 * those applications actually serve above it, the outcome those products are
 * funded to move at the top, and the teams and money that sit under the whole
 * thing. Someone who has never heard of the product should be able to look at
 * this for three seconds and know what it does.
 *
 * Deliberately not an <img>. It is drawn from the same accent variables as the
 * rest of the shell, so it recolours with the swatch picker instead of being a
 * fixed asset that stops matching the moment somebody chooses emerald.
 *
 * Nothing here is a measurement — no counts, no currency. It is a schematic,
 * and putting figures in it would make it look like a reading of the estate
 * when it is only a diagram of one.
 */

const TILE_COLUMNS = 14;
const TILE_ROWS = 5;
const TILE_W = 18;
const TILE_H = 11;
const TILE_GAP = 6;
const GRID_X = 95;
const GRID_Y = 246;

/** Tiles that read as "mapped to a product" — a fixed pattern, so every render
 *  and every screenshot of the demo shows the same picture. */
const MAPPED = new Set([2, 5, 9, 13, 16, 21, 24, 29, 31, 34, 38, 42, 45, 47, 52, 55, 58, 61, 64, 66]);

export default function HeroDiagram({ animate }: { animate: boolean }) {
  const tiles = useMemo(
    () =>
      Array.from({ length: TILE_COLUMNS * TILE_ROWS }, (_, index) => ({
        index,
        x: GRID_X + (index % TILE_COLUMNS) * (TILE_W + TILE_GAP),
        y: GRID_Y + Math.floor(index / TILE_COLUMNS) * (TILE_H + TILE_GAP),
        mapped: MAPPED.has(index),
        // A staggered pulse across the grid. The multiplier is prime against
        // the tile count so the wave crosses the grid instead of marching in
        // neat rows, which reads as a system doing something rather than an
        // animation playing.
        delay: `${(((index * 37) % 71) / 71) * 5.6}s`,
      })),
    [],
  );

  return (
    <svg
      className={`hero-diagram${animate ? ' is-live' : ''}`}
      viewBox="0 0 520 412"
      role="img"
      aria-label="How the model fits together: applications map to the products they serve, products roll up to a business outcome, and teams and cost sit underneath."
    >
      <defs>
        <linearGradient id="hd-accent" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="var(--wash-from)" />
          <stop offset="100%" stopColor="var(--wash-to)" />
        </linearGradient>
      </defs>

      {/* ── Flows ─────────────────────────────────────────────────────────── */}
      {/* Drawn before the nodes so the boxes sit on top of the lines. */}
      <g className="hd-flows" fill="none" strokeWidth="1.5">
        {/* applications → products */}
        <path d="M160 242 C160 212 86 208 86 178" />
        <path d="M260 242 C260 212 260 208 260 178" />
        <path d="M360 242 C360 212 434 208 434 178" />
        {/* products → outcome */}
        <path d="M86 130 C86 96 200 96 200 64" />
        <path d="M260 130 L260 64" />
        <path d="M434 130 C434 96 320 96 320 64" />
        {/* teams and money → the estate */}
        <path d="M145 348 C145 332 200 336 200 326" />
        <path d="M375 348 C375 332 320 336 320 326" />
      </g>

      {/* ── Outcome ───────────────────────────────────────────────────────── */}
      <g className="hd-node hd-node-lead">
        <rect x="140" y="16" width="240" height="48" rx="15" />
        <text x="260" y="46" textAnchor="middle" className="hd-label-lead">
          Business outcome
        </text>
      </g>

      {/* ── Products ──────────────────────────────────────────────────────── */}
      <text x="16" y="112" className="hd-caption">
        Products
      </text>
      {[
        { x: 16, label: 'Product' },
        { x: 190, label: 'Product' },
        { x: 364, label: 'Product' },
      ].map((product, index) => (
        <g className="hd-node hd-node-product" key={product.x} style={{ animationDelay: `${index * 0.12}s` }}>
          <rect x={product.x} y="130" width="140" height="48" rx="13" />
          <text x={product.x + 70} y="159" textAnchor="middle" className="hd-label">
            {product.label}
          </text>
        </g>
      ))}

      {/* ── Applications ──────────────────────────────────────────────────── */}
      <text x="16" y="234" className="hd-caption">
        Applications
      </text>
      <g className="hd-tiles">
        {tiles.map((tile) => (
          <rect
            key={tile.index}
            x={tile.x}
            y={tile.y}
            width={TILE_W}
            height={TILE_H}
            rx="3"
            className={tile.mapped ? 'hd-tile is-mapped' : 'hd-tile'}
            style={{ animationDelay: tile.delay }}
          />
        ))}
      </g>

      {/* ── What sits underneath ──────────────────────────────────────────── */}
      {[
        { x: 60, label: 'Teams' },
        { x: 290, label: 'Cost and benefit' },
      ].map((base, index) => (
        <g className="hd-node hd-node-base" key={base.x} style={{ animationDelay: `${0.3 + index * 0.1}s` }}>
          <rect x={base.x} y="348" width="170" height="44" rx="13" />
          <text x={base.x + 85} y="375" textAnchor="middle" className="hd-label">
            {base.label}
          </text>
        </g>
      ))}
    </svg>
  );
}
